'use strict'

type RejectFn<A> = (a: A) => any
type ResolveFn<B> = (b: B) => any
type Computation<A, B> = (reject: RejectFn<A>, resolve: ResolveFn<B>) => any
type CleanupFn = (state?: any) => void

export class Task<T> {
  fork: Computation<any, T>

  cleanup: CleanupFn

  constructor(computation: Computation<any, T>, cleanup?: CleanupFn) {
    this.fork = computation
    this.cleanup =
      cleanup ||
      (() => {
        /* empty arrow function */
      })
  }

  static of<T = any>(t: T): Task<T> {
    return new Task<T>((_, resolve) => resolve(t))
  }

  of = Task.of

  static rejected<E = any, T = never>(e: E): Task<T> {
    return new Task<T>(reject => reject(e))
  }

  static empty<T = never>(): Task<T> {
    return new Task<T>(() => {
      /* empty arrow function */
    })
  }

  empty = Task.empty

  map<U>(f: (t: T) => U): Task<U> {
    const fork = this.fork
    const cleanup = this.cleanup
    return new Task<U>(
      (reject, resolve) =>
        fork(
          a => reject(a),
          t => resolve(f(t))
        ),
      cleanup
    )
  }

  rejectedMap<E>(f: (e: any) => E): Task<T> {
    const fork = this.fork
    const cleanup = this.cleanup
    return new Task<T>(
      (reject, resolve) =>
        fork(
          e => reject(f(e)),
          t => resolve(t)
        ),
      cleanup
    )
  }

  orElse<U>(f: (e: any) => Task<U>): Task<T | U> {
    const fork = this.fork
    const cleanup = this.cleanup
    return new Task<T | U>(
      (reject, resolve) =>
        fork(
          e => f(e).fork(reject, resolve),
          t => resolve(t)
        ),
      cleanup
    )
  }

  bimap<E, U>(f: (e: any) => E, g: (t: T) => U): Task<U> {
    const fork = this.fork
    const cleanup = this.cleanup
    return new Task<U>(
      (reject, resolve) =>
        fork(
          e => reject(f(e)),
          t => resolve(g(t))
        ),
      cleanup
    )
  }

  chain<U>(f: (t: T) => Task<U>): Task<U> {
    const fork = this.fork
    const cleanup = this.cleanup
    return new Task<U>(
      (reject, resolve) =>
        fork(
          e => reject(e),
          t => f(t).fork(reject, resolve)
        ),
      cleanup
    )
  }

  ap<U>(f2: any): Task<U> {
    return this.chain(f => f2.map(f))
  }
}
